import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from "@/lib/apiEndpoint";
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useDevice } from '@/hooks/useDevice';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, Check, Loader2, QrCode, Tv, LogIn } from 'lucide-react';
import { toast } from '@/lib/toast';

// Key for storing authorized team in localStorage
const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';
// SEC-M6: also persist which device_id was paired against that team so
// AuthGate can server-verify the pair via `is_device_authorized_for_team`.
const DEVICE_AUTHORIZED_DEVICE_KEY = 'team_v_authorized_device_id';

interface QRCodeLoginProps {
  onAuthorized: (teamId: string) => void;
}

export function QRCodeLogin({ onAuthorized }: QRCodeLoginProps) {
  const { device, deviceId: hookDeviceId } = useDevice();
  const deviceId = device?.id || hookDeviceId;
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isClaimingSession, setIsClaimingSession] = useState(false);
  const [status, setStatus] = useState<'idle' | 'pending' | 'authorized' | 'signing_in'>('idle');
  const [timeLeft, setTimeLeft] = useState<number>(0);

  // Log for debugging
  const logDebug = useCallback((message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    const logEntry = `[QRLogin ${timestamp}] ${message}`;
    console.log(logEntry, data || '');

    // Also store in sessionStorage for retrieval
    try {
      const logs = JSON.parse(sessionStorage.getItem('qr_login_debug') || '[]');
      logs.push({ timestamp, message, data });
      // Keep last 50 entries
      if (logs.length > 50) logs.shift();
      sessionStorage.setItem('qr_login_debug', JSON.stringify(logs));
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Claim an authenticated session after QR authorization
  const claimSession = useCallback(async (teamId: string) => {
    if (!deviceId) return;
    setIsClaimingSession(true);
    logDebug('Claiming session for device', { deviceId, teamId });

    try {
      const response = await apiFetch('device-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'claim_session', deviceId }),
      });

      const data = await response.json();
      logDebug('Claim session response', { status: response.status, success: data.success });

      if (data.error || !data.session) {
        logDebug('Failed to claim session', { error: data.error });
        toast.error('Sessie kon niet worden aangemaakt', { id: 'auth_qr_session_error', channel: 'auth' });
        // Fall back to unauthenticated mode
        setStatus('authorized');
        onAuthorized(teamId);
        return;
      }

      // Sign in the Supabase client with the bot session
      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (setSessionError) {
        logDebug('Failed to set session', { error: String(setSessionError) });
        toast.error('Sessie kon niet worden ingesteld', { id: 'auth_qr_session_error', channel: 'auth' });
        setStatus('authorized');
        onAuthorized(teamId);
        return;
      }

      logDebug('Session claimed successfully, device is now authenticated');
      toast.success('Apparaat geautoriseerd!', { id: 'auth_signin', channel: 'auth' });
      setStatus('authorized');
      onAuthorized(teamId);
    } catch (error) {
      logDebug('Exception claiming session', { error: String(error) });
      console.error('Error claiming session:', error);
      toast.error('Fout bij het aanmaken van sessie', { id: 'auth_qr_session_error', channel: 'auth' });
      setStatus('authorized');
      onAuthorized(teamId);
    } finally {
      setIsClaimingSession(false);
    }
  }, [deviceId, onAuthorized, logDebug]);

  const generateCode = useCallback(async () => {
    if (!deviceId) {
      logDebug('No deviceId available, cannot generate code');
      return;
    }

    logDebug('Generating code for device', { deviceId });
    setIsGenerating(true);
    try {
      const response = await apiFetch('device-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate', deviceId }),
      });

      const data = await response.json();
      logDebug('Generate code response', { status: response.status, data });

      if (data.error) {
        logDebug('Error generating code', { error: data.error });
        toast.error('Kon code niet genereren', { id: 'auth_qr_code_error', channel: 'auth' });
        return;
      }

      setCode(data.code);
      setExpiresAt(new Date(data.expiresAt));
      setStatus('pending');
      logDebug('Code generated successfully', { code: data.code, expiresAt: data.expiresAt });
    } catch (error) {
      logDebug('Exception generating code', { error: String(error) });
      console.error('Error generating code:', error);
      toast.error('Fout bij genereren code', { id: 'auth_qr_code_error', channel: 'auth' });
    } finally {
      setIsGenerating(false);
    }
  }, [deviceId, logDebug]);

  // Update countdown timer
  useEffect(() => {
    if (!expiresAt || status !== 'pending') return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - now) / 1000));
      setTimeLeft(remaining);

      if (remaining === 0) {
        logDebug('Code expired');
        setStatus('idle');
        setCode(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, status, logDebug]);

  // Subscribe to realtime updates for authorization
  useEffect(() => {
    if (!code || status !== 'pending') return;

    logDebug('Setting up realtime subscription for code', { code });

    const channel = supabase
      .channel(`device-auth-${code}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'device_auth_codes',
          filter: `code=eq.${code}`,
        },
        (payload) => {
          logDebug('Realtime update received', { payload });

          if (payload.new.status === 'authorized') {
            logDebug('Device authorized! Claiming session...', { teamId: payload.new.team_id });
            setStatus('signing_in');

            const teamId = payload.new.team_id;
            if (teamId) {
              // Store the authorized team in localStorage for persistence
              try {
                localStorage.setItem(DEVICE_AUTHORIZED_TEAM_KEY, teamId);
                if (deviceId) {
                  localStorage.setItem(DEVICE_AUTHORIZED_DEVICE_KEY, deviceId);
                }
                logDebug('Stored authorized team in localStorage', { teamId, deviceId });
              } catch (e) {
                logDebug('Failed to store authorized team', { error: String(e) });
              }

              // Claim an authenticated session for the device
              claimSession(teamId);
            } else {
              logDebug('WARNING: No team_id in authorization payload');
              setStatus('authorized');
            }
          }
        }
      )
      .subscribe((status) => {
        logDebug('Realtime subscription status', { status });
      });

    return () => {
      logDebug('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [code, status, onAuthorized, logDebug]);

  // Auto-generate code on mount
  useEffect(() => {
    if (deviceId && status === 'idle') {
      logDebug('Auto-generating code on mount', { deviceId });
      generateCode();
    }
  }, [deviceId, status, generateCode, logDebug]);

  const qrUrl = code
    ? `${window.location.origin}/authorize-device?code=${code}`
    : '';

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (status === 'signing_in' || isClaimingSession) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Bezig met inloggen...</h3>
            <p className="text-muted-foreground">
              Sessie wordt aangemaakt voor dit apparaat.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'authorized') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Geautoriseerd!</h3>
            <p className="text-muted-foreground">
              Dit apparaat is succesvol gekoppeld aan het team.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <Tv className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle>Koppel dit apparaat</CardTitle>
        <CardDescription>
          Scan de QR-code met je telefoon om dit apparaat te autoriseren
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isGenerating ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Code genereren...</p>
          </div>
        ) : code ? (
          <>
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-lg">
                <QRCodeSVG
                  value={qrUrl}
                  size={200}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>

            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2">
                <QrCode className="h-5 w-5 text-muted-foreground" />
                <span className="text-2xl font-mono font-bold tracking-wider">
                  {code}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Of voer deze code handmatig in
              </p>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Verloopt over: <span className="font-mono">{formatTime(timeLeft)}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={generateCode}
                disabled={isGenerating}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Vernieuw
              </Button>
            </div>

            <div className="text-center text-xs text-muted-foreground border-t pt-4">
              <p>Open de app op je telefoon en ga naar</p>
              <p className="font-medium">Instellingen → Apparaat autoriseren</p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 py-8">
            <Button onClick={generateCode} disabled={isGenerating}>
              <QrCode className="h-4 w-4 mr-2" />
              Genereer QR-code
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
